import { useInfiniteQuery, useQuery } from '@tanstack/react-query';
import { ApiResponse, useMemoizedArrayData } from 'src/hooks/api/api';
import {
  FunnelStatus,
  OTFunnelStatusListQueryParams,
  FunnelStatusListResponse,
  CrmFunnelsStatusesService,
} from 'src/api/generated';

interface UseFunnelStatusesParams extends OTFunnelStatusListQueryParams {
  enabled?: boolean;
}

interface UseFunnelStatusesResponse extends ApiResponse {
  statuses: FunnelStatus[];
  fetchNextPage: () => void;
  hasNextPage: boolean;
  isFetchingNextPage: boolean;
}

export const useFunnelStatuses = (
  params: UseFunnelStatusesParams,
  enabled?: boolean
): UseFunnelStatusesResponse => {
  const fetchStatuses = async ({
    pageParam,
  }: {
    pageParam: string | undefined;
  }): Promise<FunnelStatusListResponse> => {
    const { filterBy, searchBy, sortBy, page, pageSize = 30 } = params;

    return await CrmFunnelsStatusesService.getFunnelStatuses(
      filterBy,
      searchBy,
      sortBy,
      page,
      pageSize,
      pageParam
    );
  };

  const getNextPageParam = (lastPage: FunnelStatusListResponse) =>
    lastPage.nextPageToken;

  const {
    data,
    error,
    isLoading: loading,
    refetch,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
  } = useInfiniteQuery({
    queryKey: ['crm/funnels/statuses', params],
    queryFn: fetchStatuses,
    getNextPageParam,
    initialPageParam: undefined,
    enabled,
  });

  const statuses = useMemoizedArrayData<FunnelStatus>(
    data?.pages.flatMap((page) => page.items) || []
  );

  return {
    loading,
    error,
    statuses,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    refetch,
  };
};

interface UseFunnelStatusParams {
  id: number;
  enabled?: boolean;
}

interface UseFunnelStatusResponse extends ApiResponse {
  status: FunnelStatus | null;
}

export const useFunnelStatus = ({
  id,
  enabled = true,
}: UseFunnelStatusParams): UseFunnelStatusResponse => {
  const fetchStatus = async (): Promise<FunnelStatus> => {
    const response = await CrmFunnelsStatusesService.getFunnelStatus(id);
    return response as FunnelStatus;
  };

  const {
    data: status,
    error,
    isLoading: loading,
    refetch,
  } = useQuery({
    queryKey: ['crm/funnels/statuses', id],
    queryFn: fetchStatus,
    enabled: enabled && !!id,
  });

  return {
    status: status || null,
    loading,
    error,
    refetch,
  };
};
